import { GuardFinderError } from './guard-finder-error';

/**
 * A generic Result class to represent the outcome of an operation.
 * Encapsulates either a successful result with data or a failure with error objects.
 * Provides methods to check the state and access data safely.
 */
export class UnitResult<R, E extends GuardFinderError = GuardFinderError> {
    /**
     * Private constructor to enforce creation through static factory methods.
     * @param result - The result data for a successful operation, or null for failure.
     * @param errors - The list of Error objects for a failed operation, or empty for success.
     */
    private constructor(
        private readonly result: R | null | undefined,
        private readonly errors: E[]
    ) {
        if (result !== undefined && errors.length > 0) {
            throw new GuardFinderError('Result cannot have both data and errors');
        }

        if (result === undefined && errors.length === 0) {
            throw new GuardFinderError('Result must have either data or errors');
        }

        errors.forEach(error => {
            if (!error.message) {
                throw new GuardFinderError('Error must have a message');
            }
        });
    }

    /**
     * Creates a successful Result with data.
     * @param data - The result data.
     * @returns A successful Result instance.
     */
    static success<T>(data: T): UnitResult<T> {
        return new UnitResult<T>(data, []);
    }

    /**
     * Creates a failed Result with error objects.
     * @param errors - The list of Error objects.
     * @returns A failed Result instance.
     */
    static failure<T, E extends GuardFinderError = GuardFinderError>(errors: E[]): UnitResult<T> {
        return new UnitResult<T, E>(undefined, errors);
    }

    /**
     * Checks if the Result represents a successful operation.
     * @returns True if the Result is successful (has data and no errors), false otherwise.
     */
    isSuccess(): boolean {
        return this.result !== undefined && this.errors.length === 0;
    }

    /**
     * Checks if the Result represents a failed operation.
     * @returns True if the Result has errors, false otherwise.
     */
    isError(): boolean {
        return this.errors.length > 0;
    }

    /**
     * Gets the first error, if any.
     * @returns The first Error object, or null if there are no errors.
     */
    getFirstError(): E | null {
        return this.errors.length > 0 ? this.errors[0] : null;
    }

    /**
     * Gets all error objects.
     * @returns The list of Error objects, or an empty array if there are none.
     */
    getErrors(): E[] {
        return [...this.errors]; // Return a copy to maintain immutability
    }

    /**
     * Gets the result data, throwing an error if the Result is not successful.
     * @returns The result data if successful.
     * @throws GuardFinderError if the Result is a failure.
     */
    getResult(): R {
        if (!this.isSuccess()) {
            const firstError = this.getFirstError();
            throw new GuardFinderError(firstError ? firstError.message : 'Cannot access result: operation failed');
        }
        return this.result as R;
    }
}
